import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Lock, Mail, ArrowLeft, Send, HelpCircle } from 'lucide-react';

// Usar URL relativa para evitar problemas de CORS
const API = '/api';

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const { access_token, user } = response.data;
      toast.success('Login realizado com sucesso!');
      onLogin(user, access_token);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Digite seu e-mail'); return; }
    setSendingReset(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(response.data.message || 'E-mail de recuperação enviado!');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Erro ao enviar e-mail de recuperação');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full overflow-hidden">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-primary">
        <img
          src="https://images.unsplash.com/photo-1656626523258-5880402224ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2xhciUyMHBhbmVscyUyMGFyY2hpdGVjdHVyZSUyMGRldGFpbCUyMGRhcmt8ZW58MHx8fHwxNzcyMzQwOTgxfDA&ixlib=rb-4.1.0&q=85"
          alt="Arquitetura moderna com integração solar"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-primary/40" />

        <div className="relative z-10 p-12 pt-16">
          <img
            src="/solaric-logo-white.svg"
            alt="SOLARIC"
            className="h-9 w-auto"
            data-testid="login-hero-logo"
          />
          <p className="text-slate-400 text-sm mt-3 tracking-wide">Energia &amp; Infraestrutura</p>
        </div>

        <div className="relative z-10 p-12 pb-16">
          <blockquote className="max-w-md">
            <p className="text-2xl font-light text-white/90 leading-relaxed mb-6" style={{ fontFamily: 'Jost, sans-serif' }}>
              Gestão inteligente de obras, equipes e operações.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-[1px] bg-secondary" />
              <p className="text-secondary text-sm font-medium tracking-wider uppercase">
                Implantação Solaric
              </p>
            </div>
          </blockquote>

          <p className="mt-12 text-slate-500 text-2xs leading-relaxed max-w-sm">
            &copy;{new Date().getFullYear()} Todos os direitos reservados para SOLARIC ENERGIA FOTOVOLTAICA INDUSTRIA E SERVICO LTDA
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex flex-col items-center justify-center bg-slate-900 relative min-h-screen lg:min-h-0 px-6 py-12">
        {/* Subtle radial glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/15 blur-[120px]" />
        </div>

        <div className="relative z-10 w-full max-w-[400px]">
          {/* Logo + Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center mb-6">
              <img
                src="/solaric-mark-white.svg"
                alt="SOLARIC"
                className="h-16 w-auto"
                data-testid="login-form-logo"
              />
            </div>
            <h1
              className="text-3xl font-bold text-white tracking-tight mb-2"
              style={{ fontFamily: 'Jost, sans-serif' }}
            >
              Bem-vindo
            </h1>
            <p className="text-slate-400 text-sm">
              Acesse sua conta para continuar
            </p>
          </div>

          {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider pl-1">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${focusedField === 'email' ? 'text-secondary' : 'text-slate-500'}`} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full h-12 pl-11 pr-4 bg-slate-800 border border-slate-700/60 rounded-xl text-white placeholder:text-slate-500 text-sm transition-all duration-200 focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/40 focus:shadow-[0_0_15px_-3px_rgba(198,232,79,0.15)]"
                    placeholder="seu@email.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider pl-1">
                  Senha
                </label>
                <div className="relative">
                  <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${focusedField === 'password' ? 'text-secondary' : 'text-slate-500'}`} />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full h-12 pl-11 pr-4 bg-slate-800 border border-slate-700/60 rounded-xl text-white placeholder:text-slate-500 text-sm transition-all duration-200 focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/40 focus:shadow-[0_0_15px_-3px_rgba(198,232,79,0.15)]"
                    placeholder="Digite sua senha"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-secondary text-primary rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 hover:bg-secondary/90 hover:shadow-lg hover:shadow-secondary/20 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                style={{ fontFamily: 'Jost, sans-serif' }}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Entrando...
                  </span>
                ) : 'Entrar'}
              </button>
            </form>
        </div>

        {/* Rodapé - copyright (mobile) + Powered by Evergrid (todas as telas) */}
        <div className="absolute bottom-5 left-0 right-0 text-center px-6">
          <p className="lg:hidden text-slate-600 text-xs mb-1.5">&copy;{new Date().getFullYear()} Todos os direitos reservados para SOLARIC ENERGIA FOTOVOLTAICA INDUSTRIA E SERVICO LTDA</p>
          <a
            href="https://www.evergrid.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-block text-slate-500 text-xs"
          >
            <span className="transition-colors duration-200 group-hover:text-secondary group-active:text-secondary">Powered by</span> <span className="font-semibold">Evergrid</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
